import { Link } from 'react-router-dom'

const guide = {
  slug: 'porn-blocker-that-blocks-vpn',
  category: 'holds',
  kicker: 'VPN · Bypass',
  title: 'A porn blocker that blocks VPNs: what "VPN-proof" actually requires',
  seoTitle: 'Porn Blocker That Blocks VPNs: What Actually Works',
  description: `A VPN walks around any DNS or router filter by replacing the resolver. What "VPN-proof" really needs on each device, and the setup where a VPN can't be added.`,
  published: '2026-09-03',
  minutes: 7,
  intro: `You set up a filter, it worked for a week, and then you remembered
          that VPN app. Every person who has tried to block porn on their own
          devices knows this story, and most blockers don't mention it. This
          guide explains why a VPN beats almost every filter, what
          "VPN-proof" has to mean technically, and how it's done on each
          device, including where it can't be.`,
  next: ['dns-porn-blocker', 'does-incognito-bypass-porn-blockers', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>Why a VPN beats a filter</h2>
      <p>
        A DNS filter, a router setting, or a filtering app all work by
        standing between your device and the internet and refusing certain
        answers. A VPN builds a tunnel from the device to the VPN provider
        and sends everything through it, including the DNS questions. The
        filter is still there; it's just no longer in the path. Nothing is
        hacked. You installed an app and tapped connect.
      </p>
      <p>
        This is why "we block VPN websites" is only half an answer: blocking
        the VPN provider's <em>website</em> doesn't remove the VPN <em>app</em>
        from the App Store, and it doesn't stop a tunnel that's already
        configured.
      </p>

      <h2>What "VPN-proof" has to mean</h2>
      <p>There are only three real ways to make a filter hold against a VPN:</p>
      <ol>
        <li><b>Forbid adding a VPN at the device level.</b> Not the website, the capability. Only the operating system can do that.</li>
        <li><b>Refuse the VPN's traffic on the network.</b> Firewall rules that block VPN protocols. Possible on a computer you administer, or on a router; fragile, because you administer it.</li>
        <li><b>Block the category "VPN / proxy / anonymizer" at DNS level.</b> Stops the VPN app from reaching its servers by name, on a device that still uses your resolver. Useful, but a VPN that is already connected has already replaced that resolver.</li>
      </ol>
      <p>
        Only the first one is a lock. The other two are layers you can add on
        top; they don't hold on their own for the person who set them up.
      </p>

      <h2>iPhone and iPad: the door that can be removed entirely</h2>
      <p>
        Apple lets a configuration profile forbid <b>creating VPN
        configurations</b>, but only on a <b>supervised</b> device. On a
        supervised iPhone or iPad, the setting isn't hidden; it's
        unavailable. The VPN app installs, opens, and cannot add its tunnel.
        The same profile carries the filtering DNS resolver and, on a
        supervised device, cannot be removed from the phone: the Remove
        Profile button doesn't exist.
      </p>
      <p>
        That combination is what SELFCNTRL installs on iPhone and iPad.
        Filtering by category, every app, every browser, private mode
        included; VPN configurations forbidden; profile non-removable.
        Loosening anything goes through your account and waits twenty-four
        hours. How supervision works, and what it does and doesn't do to your
        phone: <Link to="/guides/supervised-iphone-explained">supervised iPhone explained</Link>.
        Ten-minute setup: <Link to="/guides/how-to-block-porn-on-iphone">the iPhone guide</Link>.
      </p>

      <h2>Mac and Windows: honest limits</h2>
      <p>
        Neither Apple nor Microsoft offers supervision for a personal
        computer the way iOS does. On a Mac or a PC you own, you are the
        administrator, and an administrator can install a VPN, change DNS, or
        remove an app. A blocker that claims to be unbeatable on a computer
        you administer is describing a lock whose key is in your pocket.
      </p>
      <p>
        What can be done on a computer, and what SELFCNTRL does: the filter
        installs as an app tied to your account rather than to a setting, it
        enforces safe search, it adds curfews that cut the internet on a
        schedule, and any loosening waits twenty-four hours while tightening
        is immediate. That delay is the point. A VPN you'd install at 2am is
        an impulse; an impulse that has to wait until tomorrow afternoon
        usually doesn't get acted on. Device details:
        <Link to="/guides/how-to-block-porn-on-mac"> Mac</Link>,
        <Link to="/guides/how-to-block-porn-on-windows"> Windows</Link>.
      </p>
      <p>
        In practice, the phone is the device that's with you at night, and
        the phone is where the door can be shut for real. Lock the phone
        first.
      </p>

      <h2>Router-level "VPN blocking"</h2>
      <p>
        Some routers and filtering resolvers offer a "block VPN and proxy"
        category. It stops VPN apps from resolving their servers while the
        device still uses your resolver, and it's worth turning on for a
        household. It does nothing for a phone on mobile data, and nothing
        once a tunnel is up. See
        <Link to="/guides/how-to-block-porn-on-wifi-router"> the router guide</Link>.
      </p>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th>Setup</th><th>VPN app installs?</th><th>VPN connects?</th><th>Filter survives?</th></tr>
        </thead>
        <tbody>
          <tr><td>DNS set by you (any device)</td><td>Yes</td><td>Yes</td><td>No</td></tr>
          <tr><td>Router with VPN category blocked</td><td>Yes</td><td>Often not on Wi-Fi; yes on mobile data</td><td>Partly</td></tr>
          <tr><td>Mac / Windows, you're admin</td><td>Yes</td><td>Yes</td><td>Only with a delay you can't skip</td></tr>
          <tr><td>Supervised iPhone / iPad</td><td>Yes</td><td>No — configuration forbidden</td><td>Yes</td></tr>
        </tbody>
      </table>

      <h2>The bet</h2>
      <p>
        On a device we locked, if you get the filter off by your own means,
        VPN included, we send you $100 on top of a full refund. Details in
        the <Link to="/remboursement">refund policy</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Does a VPN bypass a porn blocker?',
      a: `A VPN bypasses any blocker that works by DNS or network filtering
          on an ordinary device, because it replaces the resolver and
          tunnels the traffic. It does not bypass a supervised iPhone or
          iPad whose profile forbids VPN configurations: the VPN app cannot
          set up its tunnel at all.`
    },
    {
      q: 'Can I block VPN apps from being installed on iPhone?',
      a: `Screen Time can restrict installing apps altogether, but that's a
          passcode you hold. A supervised profile can forbid the VPN
          capability itself, whatever apps are installed, and cannot be
          removed from the device.`
    },
    {
      q: 'Is there a VPN-proof porn blocker for Windows?',
      a: `Not in the strict sense: on a PC you administer, you can install a
          VPN. What holds on Windows is a filter attached to your account
          with a delay to loosen it, plus curfews. The unremovable lock is
          on iPhone and iPad, and most relapses happen on the phone.`
    },
    {
      q: 'What about Tor or a proxy?',
      a: `Same family as a VPN: a different path around the resolver. On a
          supervised iPhone, the filter is on the device and proxies fall
          under the same category blocking; on a computer, the answer is the
          same as for a VPN — the delay, not the wall.`
    }
  ]
}

export default guide
